import React from 'react';
import PropTypes from 'prop-types';
import PopOver from '@prisjakt/ui-popover';
import { AppContext } from '@prisjakt/ui-app-context';
import T from '@prisjakt/ui-translate';

import AlternativePriceRow from './components/alternative-price-row';
import ExpandedInfo from './components/expanded-info';
import ExpandButton from './components/expand-button';
import StoreInfoColumn from './components/store-info-column';
import StockStatusColumn from './components/stock-status-column';
import PriceColumn from './components/price-column';
import GoToStoreButton from './components/go-to-store-button';
import ReportButton from './components/report-button';
import Viewport from '../viewport';

import * as S from './styles';
import * as Table from './styles/table';

export const columnCount = 6;

const reportRowType = {
  NONE: 'none',
  DEFAULT: 'default',
  ALTERNATIVE: 'alternative',
};

class PriceRow extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      expanded: false,
      showDisabledInfo: false,
      priceRowReport: reportRowType.NONE,
    };
    this.priceRow = React.createRef();
    this.clickEvent = e => this.handleOutSideClick(e);
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.clickEvent);
    document.removeEventListener('visibilitychange', this.onRefocus);
  }

  handleOutSideClick(e) {
    if (
      this.priceRow &&
      this.priceRow.current &&
      this.priceRow.current.contains(e.target)
    ) {
      return;
    }
    window.removeEventListener('click', this.clickEvent);
    this.setState({ showDisabledInfo: false });
  }

  toggleDisabledInfo = () => {
    this.setState(
      ({ showDisabledInfo }) => ({ showDisabledInfo: !showDisabledInfo }),
      () => {
        if (this.state.showDisabledInfo) {
          window.addEventListener('click', this.clickEvent);
        } else {
          window.removeEventListener('click', this.clickEvent);
        }
      }
    );
  };

  toggleExpanded = () =>
    this.setState(state => ({ expanded: !state.expanded }));

  handleClickOut = (e, id, isAlternative = false) => {
    const {
      isInternationalPrice,
      externalUri,
      onPriceClick,
      store,
    } = this.props;
    const { showDisabledInfo } = this.state;
    if (externalUri) {
      if (IS_BROWSER) {
        this.startBounceTimer(id);
      }
      if (!showDisabledInfo && !e.defaultPrevented) {
        onPriceClick(
          store && store.featured,
          undefined,
          isInternationalPrice ? 'international' : undefined,
          isAlternative
        );
      }
    } else {
      e.preventDefault();
      this.toggleDisabledInfo();
    }
  };

  handleClickOutAltPrice = (e, id) => {
    this.setState({ priceRowReport: reportRowType.ALTERNATIVE });
    this.handleClickOut(e, id, true);
  };

  onClickOut = e => {
    this.setState({ priceRowReport: reportRowType.DEFAULT });
    this.handleClickOut(e, this.props.priceId);
  };

  startBounceTimer(id) {
    // Start a timer when a user clicks out on a product
    // if they return to Prisjakt within 15 seconds
    // display an error report button in the price row.

    const startTime = Date.now();

    this.onRefocus = () => {
      if (
        Date.now() - startTime > 15000 &&
        document.visibilityState === 'visible'
      ) {
        document.removeEventListener('visibilitychange', this.onRefocus);
        return;
      }

      if (document.visibilityState === 'visible') {
        this.props.setReportRowId(id);

        // Action event for tracking
        this.props.onClickOutBounce(this.props.productId, this.props.priceId);

        document.removeEventListener('visibilitychange', this.onRefocus);
      }
    };

    document.addEventListener('visibilitychange', this.onRefocus);
  }

  closeReportOverlay = () => {
    this.setState({ priceRowReport: 'none' });
    this.props.setReportRowId(null);
  };

  render() {
    const {
      externalUri,
      isInternationalPrice,
      showInternational,
      openInNewWindow,
      brandRecommendedRetailer,
      brand,
      store,
      storeInfo,
      alternativePrices,
      storeBadges,
      priceId,
      priceType,
      deliveryOptions = [],
      price,
      campaignStyling,
      stock,
      reportRowId,
      productName,
      media,
      currentSort,
      name,
      removeDuplicateAlternativePriceRow,
    } = this.props;

    const { t } = this.context;
    const { expanded, showDisabledInfo } = this.state;
    const featured = store.featured;
    const tableLinkProps = {
      rel: 'nofollow noopener',
      href: externalUri,
      onClick: this.onClickOut,
      target: openInNewWindow ? '_blank' : '',
      disabled: !externalUri,
    };

    return (
      <>
        <Table.Row
          // Classname is used to track click out
          className="pj-ui-price-row"
          nonFeatured={!featured}
          noExternalUri={!externalUri}
          nonFeaturedWithExternalUri={!featured && externalUri}
          showReport={reportRowId === priceId}
          data-test="PriceRow"
          ref={this.priceRow}
          key={store.id}
        >
          <Table.Column>
            <ExpandButton
              title={t('product', 'price.more-info')}
              onClick={this.toggleExpanded}
              expanded={expanded}
            />
          </Table.Column>
          <Table.Column>
            <Table.Link {...tableLinkProps} alignLeft>
              <StoreInfoColumn
                store={store}
                userReviewSummary={store.userReviewSummary}
                brandRecommendedRetailer={false}
              />
            </Table.Link>
          </Table.Column>
          <Table.Column expand>
            <Table.Link {...tableLinkProps} alignLeft>
              <S.ProductNameColumn>
                <Viewport desktop>
                  <Viewport desktop>
                    <S.ProductName>{name}</S.ProductName>
                  </Viewport>

                  {brandRecommendedRetailer && (
                    <S.RecommendedRetailer>
                      <S.RecommendedRetailerIcon />
                      <T domain="store">brand.recommended-retailer</T>
                    </S.RecommendedRetailer>
                  )}
                </Viewport>
              </S.ProductNameColumn>
            </Table.Link>
          </Table.Column>
          <Table.Column>
            <Table.Link {...tableLinkProps} alignCenter>
              <StockStatusColumn
                status={stock.status}
                statusText={stock.statusText}
                deliveryOptions={deliveryOptions}
                storeMarket={store.market}
                countryCode={store.countryCode}
                toneDown={!store.featured}
                isInternationalPrice={isInternationalPrice}
                showInternational={showInternational}
                showTooltip
                priceType={priceType}
              />
            </Table.Link>
          </Table.Column>
          <Table.Column>
            <Table.Link {...tableLinkProps} alignRight>
              <PriceColumn
                price={price}
                storeIsFeatured={store.featured}
                campaignStyling={campaignStyling}
                isInternationalPrice={isInternationalPrice}
                align="right"
                currentSort={currentSort}
              />
            </Table.Link>
          </Table.Column>
          <Table.Column>
            {externalUri ? (
              <Table.Link {...tableLinkProps}>
                <GoToStoreButton
                  visible
                  noPadding
                  disabled={!featured}
                  featured={featured}
                />
              </Table.Link>
            ) : (
              <PopOver
                id={`click-out-${priceId}`}
                open={Boolean(showDisabledInfo)}
                width={250}
                offsetY={-35}
                offsetX={40}
                arrowOffsetY={35}
                position="left"
                label={t('product', 'transparency.prices-more-info-button')}
                onClose={this.toggleDisabledInfo}
              >
                <Table.Link {...tableLinkProps}>
                  <GoToStoreButton
                    visible={Boolean(externalUri || featured)}
                    disabled={Boolean(externalUri && !featured)}
                    featured={featured}
                  />
                </Table.Link>
                <S.TransparencyShopInfo>
                  <T domain="product">transparency.store-info</T>
                </S.TransparencyShopInfo>
              </PopOver>
            )}
          </Table.Column>
        </Table.Row>
        {expanded && (
          <Table.InfoRow data-test="ColumnExpanded">
            <td colSpan="6">
              <ExpandedInfo
                brand={brand}
                brandRecommendedRetailer={brandRecommendedRetailer}
                storeName={store.name}
                storeLink={store.pathName}
                storeInfo={storeInfo}
                storeBadges={storeBadges}
                price={price}
                priceType={priceType}
                currentSort={currentSort}
              />
            </td>
          </Table.InfoRow>
        )}
        {expanded &&
          alternativePrices.map(altPrice => {
            // Don't render alternative price row if it is the same as default price row.
            if (removeDuplicateAlternativePriceRow && altPrice.id === priceId)
              return null;
            return (
              <>
                <AlternativePriceRow
                  key={altPrice.id}
                  campaignStyling={campaignStyling}
                  externalUri={externalUri}
                  onClickOut={this.handleClickOutAltPrice}
                  isInternationalPrice={isInternationalPrice}
                  price={altPrice}
                  priceStore={store}
                  tableLinkProps={tableLinkProps}
                  currentSort={currentSort}
                />
                {this.state.priceRowReport === reportRowType.ALTERNATIVE &&
                  reportRowId === altPrice.id && (
                    <Table.AltReportRow expanded={expanded} isAlternative>
                      <Table.Column colSpan="6">
                        <ReportButton
                          priceStore={store}
                          priceId={altPrice.id}
                          priceValue={altPrice.price.exclShipping}
                          stockStatus={altPrice.stock.status}
                          productImage={media.first}
                          onClose={this.closeReportOverlay}
                          productName={productName}
                        />
                      </Table.Column>
                    </Table.AltReportRow>
                  )}
              </>
            );
          })}
        {this.state.priceRowReport === reportRowType.DEFAULT &&
          reportRowId === priceId && (
            <Table.ReportRow>
              <Table.Column colSpan="6">
                <ReportButton
                  priceStore={store}
                  priceId={priceId}
                  priceValue={price.exclShipping}
                  stockStatus={stock.status}
                  productImage={media.first}
                  onClose={this.closeReportOverlay}
                  productName={productName}
                />
              </Table.Column>
            </Table.ReportRow>
          )}
      </>
    );
  }
}

const {
  string,
  number,
  bool,
  arrayOf,
  oneOf,
  oneOfType,
  shape,
  func,
} = PropTypes;

PriceRow.contextType = AppContext;

PriceRow.propTypes = {
  store: shape({
    id: number,
    name: string,
    featured: bool,
    hasLogo: bool,
    pathName: string,
    market: string,
    countryCode: string,
    primaryMarket: string,
  }).isRequired,
  storeInfo: arrayOf(string),
  storeBadges: arrayOf(
    shape({
      id: number.isRequired,
      name: string.isRequired,
      sentiment: oneOf(['positive', 'negative']),
      nrOfPositiveReviews: number.isRequired,
      nrOfReviews: number.isRequired,
    })
  ),
  priceId: oneOfType([string, number]).isRequired,
  productId: oneOfType([string, number]).isRequired,
  name: string,
  productName: string,
  media: shape({
    images: arrayOf(string),
    large: arrayOf(string),
    videos: arrayOf(
      shape({
        source_id: string,
        embed_url: string,
        thumbnail: string,
      })
    ),
  }),
  priceType: string,
  price: shape({
    inclShipping: number,
    exclShipping: number,
    originalCurrency: string,
    originalCurrencyExclShipping: string,
  }).isRequired,
  campaignStyling: string,
  stock: shape({
    status: string,
    statusText: string,
  }),
  externalUri: string,
  knownShippingPrice: bool.isRequired,
  isInternationalPrice: bool,
  showInternational: bool,
  brandRecommendedRetailer: bool,
  brand: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    logo: PropTypes.string,
  }),
  onPriceClick: func,
  alternativePrices: arrayOf(
    shape({
      id: string.isRequired,
      name: string,
      type: string,
      externalUri: string,
      stock: shape({
        status: string,
        statusText: string,
      }),
      price: shape({
        inclShipping: number,
        exclShipping: number,
        originalCurrency: string,
        originalCurrencyExclShipping: string,
      }),
    })
  ),
  openInNewWindow: bool.isRequired,
  onClickOutBounce: func.isRequired,
  setReportRowId: func,
  reportRowId: string,
  currentSort: string.isRequired,
  removeDuplicateAlternativePriceRow: bool,
};

PriceRow.defaultProps = {
  onPriceClick: () => {},
  alternativePrices: [],
  externalUri: undefined,
  storeInfo: null,
  brand: null,
  isInternationalPrice: false,
  showInternational: false,
  brandRecommendedRetailer: false,
  storeBadges: [],
  priceType: undefined,
  campaignStyling: null,
  stock: undefined,
  name: undefined,
  productName: undefined,
  media: undefined,
  setReportRowId: () => {},
  reportRowId: null,
  removeDuplicateAlternativePriceRow: false,
};

export default PriceRow;
